import React from 'react';
import '../../../../css/role-style.css';
import { Button, Card, Checkbox, Form, Input, Select } from 'antd';
import { StaffRoleUpdateInput } from '../../../../generated';

interface PermissionCheckboxProps {
  name: string[];
  label: string;
  nested?: boolean;
  disabled?: boolean;
  onChange?: (e: any) => void;
}

const casePermissionFields = [
  'canViewUnassignedCases',
  'canViewCasesAssignedToOthers',
  'canBeAssignedCases',
  'canAssignSelf',
  'canUnassignSelf',
  'canAssignCaseWorker',
  'canUnassignCaseWorker',
  'canIssueRefunds',
  'canViewCaseHistory',
  'canReopenCases'
];
const permissionSections = {
  idCase: {
    path: ['permissions', 'idCasePermissions'],
    searchField: 'canSearchCases',
    fields: casePermissionFields
  },
  credCase: {
    path: ['permissions', 'credCasePermissions'],
    searchField: 'canSearchCases',
    fields: casePermissionFields
  },
  sendCase: {
    path: ['permissions', 'sendCasePermissions'],
    searchField: 'canSearchCases',
    fields: casePermissionFields
  },
  role: {
    path: ['permissions', 'rolePermissions'],
    searchField: 'canSearchRoles',
    fields: ['canAddRoles', 'canEditRoles']
  },
  staff: {
    path: ['permissions', 'staffUserPermissions'],
    searchField: 'canSearchStaff',
    fields: ['canAssignRoles', 'canBlockAccess', 'canAddOrRemoveTags']
  },
  applicant: {
    path: ['permissions', 'applicantUserPermissions'],
    searchField: 'canSearchApplicants',
    fields: ['canUpdatePersonalInformation', 'canBlockAccess', 'canAddOrRemoveTags']
  },
  entity: {
    path: ['permissions', 'entityPermissions'],
    searchField: 'canSearchEntities',
    fields: ['canAddEntities', 'canEditEntities', 'canDeleteEntities']
  }
};

const PermissionCheckbox: React.FC<PermissionCheckboxProps> = ({ name, label, nested, onChange }) => (
  <div className={`formBox ${nested ? 'deep' : ''}`}>
    <Form.Item name={name} valuePropName="checked" className="m-0 p-0">
      <Checkbox onChange={onChange}>{label}</Checkbox>
    </Form.Item>
  </div>
);

const { Option } = Select;
interface RoleCreateProps {
  onSubmit: (payload: any) => void;
  roleSubtype: string;
  loading: boolean;
}

export const RoleCreate: React.FC<RoleCreateProps> = (props) => {
  const dropDownValue = [
    {
      label: 'IS',
      value: 'Staff.IS'
    },
    {
      label: 'Case Worker',
      value: 'Staff.CaseWorker'
    },
    {
      label: 'Finance',
      value: 'Staff.Finance'
    },
    {
      label: 'ReadOnly',
      value: 'Staff.ReadOnly'
    }
  ];

  const [form] = Form.useForm<StaffRoleUpdateInput>();
  const [formLoading, setFormLoading] = React.useState(false);

  const unCheckPermissions = (section: keyof typeof permissionSections) => {
    const config = permissionSections[section];
    const update = {
      permissions: {
        [config.path[1]]: {
          [config.searchField]: false,
          ...Object.fromEntries(config.fields.map((field) => [field, false]))
        }
      }
    };
    form.setFieldsValue(update);
  };

  const onPermissionChange = (changedValues: any) => {
    for (const [section, config] of Object.entries(permissionSections)) {
      const changes = changedValues.permissions?.[config.path[1]];
      if (!changes) {
        continue;
      }

      // select search permission if any other permission is selected
      if (!Object.keys(changes).some((key) => key.includes('canSearch'))) {
        form.setFieldsValue({ permissions: { [config.path[1]]: { [config.searchField]: true } } });
      }
      // unselect all permissions if search permission is unselected
      if (changes[config.searchField] === false) {
        unCheckPermissions(section as keyof typeof permissionSections);
      }
      return;
    }
  };

  return (
    <div className="container">
      <div className="pageHeading"></div>
      <Form
        form={form}
        onFinish={(v) => {
          setFormLoading(true);
          props.onSubmit(v);
          setFormLoading(false);
        }}
        onValuesChange={onPermissionChange}
        layout="vertical"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'right',
          justifyContent: 'end'
        }}
      >
        <Card style={{ marginBottom: '10px', padding: '20px 50px' }}>
          <div className="roleContainer">
            <div className="roleInfo">
              <div className="sectionName">
                <h3>Role Info</h3>
              </div>
              <div className="roleInputs">
                <div className="formBox">
                  <Form.Item label="Role Name" style={{ width: '100%' }} rules={[{ required: true, message: 'Role Name is Required' }]} name={'roleName'}>
                    <Input />
                  </Form.Item>
                </div>
                {props?.roleSubtype === 'Staff.IS' ? (
                  <div className="formBox">
                    <Form.Item
                      label="Role Sub Type"
                      style={{ width: '100%' }}
                      rules={[
                        {
                          required: true,
                          message: 'Role Sub type is Required'
                        }
                      ]}
                      name={'roleSubtype'}
                    >
                      <Select>
                        {dropDownValue?.map((subRole) => (
                          <Option value={subRole.value}>{subRole.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            </div>
            <div className="rolePermission">
              <div className="sectionName">
                <h3>Permissions</h3>
              </div>
              <div className="roleInputs">
                {/* ID Case */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>ID Case</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox name={['permissions', 'idCasePermissions', 'canSearchCases']} label="Can Search Cases" />
                    <PermissionCheckbox name={['permissions', 'idCasePermissions', 'canViewUnassignedCases']} label="Can View Unassigned Cases" />
                    <PermissionCheckbox name={['permissions', 'idCasePermissions', 'canViewCasesAssignedToOthers']} label="Can View Cases Assigned to Others" />
                  </div>
                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox
                      name={['permissions', 'idCasePermissions', 'canBeAssignedCases']}
                      label="Can Be Assigned Cases"
                      nested={true}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        if (!isChecked) {
                          form.setFieldsValue({
                            permissions: {
                              idCasePermissions: {
                                canAssignSelf: false,
                                canUnassignSelf: false
                              }
                            }
                          });
                        }
                      }}
                    />
                    <PermissionCheckbox
                      name={['permissions', 'idCasePermissions', 'canAssignSelf']}
                      label="Can Assign Self"
                      nested={true}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        if (isChecked) {
                          form.setFieldsValue({
                            permissions: {
                              idCasePermissions: {
                                canBeAssignedCases: true
                              }
                            }
                          });
                        }
                      }}
                    />
                    <PermissionCheckbox
                      name={['permissions', 'idCasePermissions', 'canUnassignSelf']}
                      label="Can Unassign Self"
                      nested={true}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        if (isChecked) {
                          form.setFieldsValue({
                            permissions: {
                              idCasePermissions: {
                                canBeAssignedCases: true
                              }
                            }
                          });
                        }
                      }}
                    />
                    <PermissionCheckbox name={['permissions', 'idCasePermissions', 'canAssignCaseWorker']} label="Can Assign Case Worker" />
                    <PermissionCheckbox name={['permissions', 'idCasePermissions', 'canUnassignCaseWorker']} label="Can Unassign Case Worker" />
                  </div>

                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox name={['permissions', 'idCasePermissions', 'canIssueRefunds']} label="Can Issue Refund" />
                    <PermissionCheckbox name={['permissions', 'idCasePermissions', 'canViewCaseHistory']} label="Can View Case History" />
                    <PermissionCheckbox name={['permissions', 'idCasePermissions', 'canReopenCases']} label="Can Reopen Case" />
                  </div>
                </div>

                {/* Verification Case */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Verification Case</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox name={['permissions', 'credCasePermissions', 'canSearchCases']} label="Can Search Cases" />
                    <PermissionCheckbox name={['permissions', 'credCasePermissions', 'canViewUnassignedCases']} label="Can View Unassigned Cases" />
                    <PermissionCheckbox name={['permissions', 'credCasePermissions', 'canViewCasesAssignedToOthers']} label="Can View Cases Assigned to Others" />
                  </div>
                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox
                      name={['permissions', 'credCasePermissions', 'canBeAssignedCases']}
                      label="Can Be Assigned Cases"
                      nested={true}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        if (!isChecked) {
                          form.setFieldsValue({
                            permissions: {
                              credCasePermissions: {
                                canAssignSelf: false,
                                canUnassignSelf: false
                              }
                            }
                          });
                        }
                      }}
                    />
                    <PermissionCheckbox
                      name={['permissions', 'credCasePermissions', 'canAssignSelf']}
                      label="Can Assign Self"
                      nested={true}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        if (isChecked) {
                          form.setFieldsValue({
                            permissions: {
                              credCasePermissions: {
                                canBeAssignedCases: true
                              }
                            }
                          });
                        }
                      }}
                    />
                    <PermissionCheckbox
                      name={['permissions', 'credCasePermissions', 'canUnassignSelf']}
                      label="Can Unassign Self"
                      nested={true}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        if (isChecked) {
                          form.setFieldsValue({
                            permissions: {
                              credCasePermissions: {
                                canBeAssignedCases: true
                              }
                            }
                          });
                        }
                      }}
                    />
                    <PermissionCheckbox name={['permissions', 'credCasePermissions', 'canAssignCaseWorker']} label="Can Assign Case Worker" />
                    <PermissionCheckbox name={['permissions', 'credCasePermissions', 'canUnassignCaseWorker']} label="Can Unassign Case Worker" />
                  </div>

                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox name={['permissions', 'credCasePermissions', 'canIssueRefunds']} label="Can Issue Refund" />
                    <PermissionCheckbox name={['permissions', 'credCasePermissions', 'canViewCaseHistory']} label="Can View Case History" />
                    <PermissionCheckbox name={['permissions', 'credCasePermissions', 'canReopenCases']} label="Can Reopen Case" />
                  </div>
                </div>

                {/* Send Case */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Send Case</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox name={['permissions', 'sendCasePermissions', 'canSearchCases']} label="Can Search Cases" />
                    <PermissionCheckbox name={['permissions', 'sendCasePermissions', 'canViewUnassignedCases']} label="Can View Unassigned Cases" />
                    <PermissionCheckbox name={['permissions', 'sendCasePermissions', 'canViewCasesAssignedToOthers']} label="Can View Cases Assigned to Others" />
                  </div>
                  <div className="permissionSubSectionInput">
                  <PermissionCheckbox
                      name={['permissions', 'sendCasePermissions', 'canBeAssignedCases']}
                      label="Can Be Assigned Cases"
                      nested={true}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        if (!isChecked) {
                          form.setFieldsValue({
                            permissions: {
                              sendCasePermissions: {
                                canAssignSelf: false,
                                canUnassignSelf: false
                              }
                            }
                          });
                        }
                      }}
                    />
                    <PermissionCheckbox
                      name={['permissions', 'sendCasePermissions', 'canAssignSelf']}
                      label="Can Assign Self"
                      nested={true}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        if (isChecked) {
                          form.setFieldsValue({
                            permissions: {
                              sendCasePermissions: {
                                canBeAssignedCases: true
                              }
                            }
                          });
                        }
                      }}
                    />
                    <PermissionCheckbox
                      name={['permissions', 'sendCasePermissions', 'canUnassignSelf']}
                      label="Can Unassign Self"
                      nested={true}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        if (isChecked) {
                          form.setFieldsValue({
                            permissions: {
                              sendCasePermissions: {
                                canBeAssignedCases: true
                              }
                            }
                          });
                        }
                      }}
                    />
                    <PermissionCheckbox name={['permissions', 'sendCasePermissions', 'canAssignCaseWorker']} label="Can Assign Case Worker" />
                    <PermissionCheckbox name={['permissions', 'sendCasePermissions', 'canUnassignCaseWorker']} label="Can Unassign Case Worker" />
                  </div>

                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox name={['permissions', 'sendCasePermissions', 'canIssueRefunds']} label="Can Issue Refund" />
                    <PermissionCheckbox name={['permissions', 'sendCasePermissions', 'canViewCaseHistory']} label="Can View Case History" />
                    <PermissionCheckbox name={['permissions', 'sendCasePermissions', 'canReopenCases']} label="Can Reopen Case" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card style={{ marginBottom: '10px', padding: '20px 50px' }}>
          <div className="roleContainer">
            <div className="rolePermission">
              <div className="roleInputs">
                {/* Role */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Role</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox name={['permissions', 'rolePermissions', 'canSearchRoles']} label="Can Search Roles" />
                    <PermissionCheckbox name={['permissions', 'rolePermissions', 'canAddRoles']} label="Can Add Roles" />
                    <PermissionCheckbox name={['permissions', 'rolePermissions', 'canEditRoles']} label="Can Edit Roles" />
                  </div>
                </div>

                {/* Staff */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Staff</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox name={['permissions', 'staffUserPermissions', 'canSearchStaff']} label="Can Search Staff" />
                    <PermissionCheckbox name={['permissions', 'staffUserPermissions', 'canAssignRoles']} label="Can Assign Roles" />
                    <PermissionCheckbox name={['permissions', 'staffUserPermissions', 'canBlockAccess']} label="Can Block Access" />
                    <PermissionCheckbox name={['permissions', 'staffUserPermissions', 'canAddOrRemoveTags']} label="Can Add/Remove Tags" />
                  </div>
                </div>

                {/* Applicants */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Applicants</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox name={['permissions', 'applicantUserPermissions', 'canSearchApplicants']} label="Can Search Applicants" />
                    <PermissionCheckbox name={['permissions', 'applicantUserPermissions', 'canUpdatePersonalInformation']} label="Can Update Personal Information" />
                    <PermissionCheckbox name={['permissions', 'applicantUserPermissions', 'canBlockAccess']} label="Can Block Access" />
                    <PermissionCheckbox name={['permissions', 'applicantUserPermissions', 'canAddOrRemoveTags']} label="Can Add/Remove Tags" />
                  </div>
                </div>

                {/* Entity */}
                <div className="permissionSubSection">
                  <div className="header">
                    <p>Entity</p>
                  </div>
                  <div className="permissionSubSectionInput">
                    <PermissionCheckbox name={['permissions', 'entityPermissions', 'canSearchEntities']} label="Can Search Entities" />
                    <PermissionCheckbox name={['permissions', 'entityPermissions', 'canAddEntities']} label="Can Add Entities" />
                    <PermissionCheckbox name={['permissions', 'entityPermissions', 'canEditEntities']} label="Can Edit Entities" />
                    <PermissionCheckbox name={['permissions', 'entityPermissions', 'canDeleteEntities']} label="Can Delete Entities" />
                  </div>
                </div>
              </div>
              <Form.Item>
                <Button loading={props?.loading || formLoading} type="primary" htmlType="submit">
                  Create Role
                </Button>
              </Form.Item>
            </div>
          </div>
        </Card>
      </Form>
    </div>
  );
};
